import { InfoIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useLaborCharting } from './context'

interface HomebirthActivationModalProps {
  isOpen: boolean
  onClose: () => void
}

const toDatetimeLocalValue = (ms: number): string => {
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

const parseDatetimeLocal = (value: string): number => new Date(value).getTime()

export const HomebirthActivationModal = ({ isOpen, onClose }: HomebirthActivationModalProps) => {
  const { activateHomebirth } = useLaborCharting()
  const [onsetValue, setOnsetValue] = useState(() => toDatetimeLocalValue(Date.now()))
  const [saving, setSaving] = useState(false)

  const handleConfirm = async () => {
    const onsetAt = parseDatetimeLocal(onsetValue)
    if (!onsetAt || isNaN(onsetAt)) return
    setSaving(true)
    try {
      await activateHomebirth(onsetAt)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={8}>
        <ModalBody p={0}>
          <Flex direction="column" gap={0}>
            {/* Header */}
            <Flex
              align="center"
              gap={2}
              px={5}
              py={4}
              borderBottom="1px solid"
              borderColor="pink.200"
              bg="pink.50"
              borderTopRadius={8}
              _dark={{ bg: 'pink.900', borderBottomColor: 'pink.700' }}>
              <Text
                fontFamily="Hero-New"
                fontWeight={700}
                fontSize="lg"
                color="pink.700"
                _dark={{ color: 'pink.200' }}>
                Open Labor &amp; Delivery Episode
              </Text>
            </Flex>

            <Flex direction="column" gap={4} px={5} py={5}>
              {/* Compliance note */}
              <Alert status="info" borderRadius={6} fontSize="sm" py={2}>
                <AlertIcon as={InfoIcon} />
                <Text fontSize="sm">
                  Confirm the labor onset time. This timestamp is the official start of the labor
                  record.
                </Text>
              </Alert>

              {/* Onset time field */}
              <Flex direction="column" gap={1}>
                <Text
                  fontFamily="Hero-New"
                  fontWeight={600}
                  fontSize="sm"
                  color="gray.700"
                  _dark={{ color: 'gray.200' }}>
                  Labor Onset Time
                </Text>
                <Input
                  type="datetime-local"
                  value={onsetValue}
                  onChange={e => setOnsetValue(e.target.value)}
                  size="md"
                  fontFamily="Hero-New"
                />
              </Flex>
            </Flex>

            {/* Footer buttons */}
            <Flex
              justify="flex-end"
              gap={3}
              px={5}
              py={4}
              borderTop="1px solid"
              borderColor="gray.100"
              _dark={{ borderTopColor: 'whiteAlpha.100' }}>
              <Button variant="ghost" fontFamily="Hero-New" onClick={onClose} isDisabled={saving}>
                Cancel
              </Button>
              <Button
                colorScheme="pink"
                fontFamily="Hero-New"
                isLoading={saving}
                isDisabled={!onsetValue}
                onClick={handleConfirm}>
                Confirm — Open Labor Charting
              </Button>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
